import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GetWorkstreams } from '../../redux/dataroomSlice';
import { DeleteGroup, GetAllGroups, PatchDataroomGroup, PostDataroomGroup } from '../../redux/teamSlice';
import { getInitials } from '../../utils/commonUtils';
import NewProjectWizardCard from './NewProjectWizardCard';
import CreateAccessGroup from './CreateAccessGroup';
import {
    createEmptyAccessDraft,
    draftFromGroup,
    featureSummary,
    isAdminGroup,
    VDR_PRESETS,
    vdrPresetTitle,
    workstreamSummary,
} from './accessGroupCatalog';
import { TEAM_SETUP_STEPS } from './ndaDraft';
import styles from './css/index.module.css';

const nextLocalId = () => `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

export default function SetupAccessGroups({
    project,
    workstreams: confirmedWorkstreams = [],
    extras: extrasProp,
    onExtrasChange,
    localGroups: localGroupsProp,
    onLocalGroupsChange,
    startCreate = false,
    onWorkstreamsChange,
    onContinue,
    onBack,
}) {
    const dispatch = useDispatch();
    const user = useSelector(state => state?.user?.user);
    const selectedProject = useSelector(state => state?.projectData?.selectedProject);
    const apiGroups = useSelector(state => state?.team?.groups);

    const [mode, setMode] = React.useState(startCreate ? 'form' : 'list');
    const [editingId, setEditingId] = React.useState(null);
    const [saving, setSaving] = React.useState(false);
    const [internalLocalGroups, setInternalLocalGroups] = React.useState([]);
    const [internalExtras, setInternalExtras] = React.useState({});
    const extras = extrasProp ?? internalExtras;
    const setExtras = onExtrasChange || setInternalExtras;
    const localGroups = localGroupsProp ?? internalLocalGroups;
    const setLocalGroups = onLocalGroupsChange || setInternalLocalGroups;
    const [workstreams, setWorkstreams] = React.useState(confirmedWorkstreams || []);

    const projectId = project?.id || selectedProject?.id;
    const vdrId = project?.vdr?.id || selectedProject?.vdr?.id;
    const canPersist = Boolean(projectId && vdrId);

    React.useEffect(() => {
        let cancelled = false;
        const load = async () => {
            if (canPersist) {
                dispatch(GetAllGroups());
            }
            if (!projectId) return;
            const listRes = await dispatch(GetWorkstreams(projectId));
            if (cancelled) return;
            const list = listRes?.payload?.data;
            if (Array.isArray(list) && list.length) {
                const mapped = list.map(ws => ({ id: ws.id, name: ws.name, acl: ws.acl }));
                setWorkstreams(mapped);
                onWorkstreamsChange?.(mapped);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [canPersist, dispatch, projectId]);

    const groups = React.useMemo(() => {
        const merged = canPersist ? [...(apiGroups || []), ...(localGroups || [])] : localGroups || [];
        const seen = new Set();
        return merged.filter(group => {
            if (!group?.id || seen.has(String(group.id))) return false;
            seen.add(String(group.id));
            return true;
        });
    }, [apiGroups, canPersist, localGroups]);
    const accessGroups = groups.filter(group => !isAdminGroup(group));

    const displayName = user?.full_name || '';
    const email = user?.email || '';
    const initials = getInitials(displayName) || (email ? email.slice(0, 2).toUpperCase() : '');

    const openCreateForm = () => {
        setEditingId(null);
        setMode('form');
    };

    const startEdit = group => {
        setEditingId(group.id);
        setMode('form');
    };

    const closeForm = () => {
        setEditingId(null);
        setMode('list');
    };

    const editingGroup = accessGroups.find(group => group.id === editingId);
    const initialDraft = editingGroup
        ? { ...(extras[editingGroup.id] || draftFromGroup(editingGroup, workstreams)), name: editingGroup.name }
        : createEmptyAccessDraft();

    const existingNames = accessGroups
        .filter(group => group.id !== editingId)
        .map(group => group.name);

    const handleSave = async draft => {
        if (saving) return;
        setSaving(true);
        const preset = VDR_PRESETS.find(item => item.key === draft.vdrPreset);
        const permissions = {
            ...(preset?.permissions || {}),
            intelligence_insights: Boolean(draft.intelligence),
            project_mail: Boolean(draft.projectMail),
            project_meetings: Boolean(draft.projectMeetings),
        };
        const extra = {
            features: draft.features,
            workstreamMode: draft.workstreamMode,
            selectedWorkstreamIds: draft.selectedWorkstreamIds,
            intelligence: draft.intelligence,
            projectMail: draft.projectMail,
            projectMeetings: draft.projectMeetings,
            vdrPreset: draft.vdrPreset,
        };

        if (!canPersist) {
            if (editingId) {
                setLocalGroups(prev => prev.map(item => (item.id === editingId ? { ...item, name: draft.name } : item)));
                setExtras(prev => ({ ...prev, [editingId]: extra }));
                setSaving(false);
                closeForm();
                return;
            }
            const id = nextLocalId();
            setLocalGroups(prev => [...prev, { id, name: draft.name, acl: draft.acls, permissions }]);
            setExtras(prev => ({ ...prev, [id]: extra }));
            setSaving(false);
            onContinue();
            return;
        }

        if (editingId) {
            const result = await dispatch(
                PatchDataroomGroup({
                    id: editingId,
                    name: draft.name,
                    vdr: vdrId,
                    permissions,
                    acls: draft.acls,
                }),
            );
            setSaving(false);
            if (result?.type !== 'PATCH_DATAROOM_GROUPS/fulfilled') return;
            setExtras(prev => ({ ...prev, [editingId]: extra }));
            closeForm();
            return;
        }

        const result = await dispatch(
            PostDataroomGroup({
                vdr: vdrId,
                name: draft.name,
                project: projectId,
                permissions,
                acls: draft.acls,
            }),
        );
        setSaving(false);
        if (result?.type !== 'POST_DATAROOM_GROUPS/fulfilled') {
            const id = nextLocalId();
            setLocalGroups(prev => [...prev, { id, name: draft.name, acl: draft.acls, permissions }]);
            setExtras(prev => ({ ...prev, [id]: extra }));
            onContinue();
            return;
        }
        const createdId = result?.payload?.data?.id;
        if (createdId) setExtras(prev => ({ ...prev, [createdId]: extra }));
        onContinue();
    };

    const handleRemove = async group => {
        if (canPersist) {
            const result = await dispatch(DeleteGroup(group.id));
            if (result?.type !== 'DELETE_GROUPS/fulfilled') return;
        } else {
            setLocalGroups(prev => prev.filter(item => item.id !== group.id));
        }
        setExtras(prev => {
            const next = { ...prev };
            delete next[group.id];
            return next;
        });
    };

    if (mode === 'form') {
        return (
            <CreateAccessGroup
                initialDraft={initialDraft}
                workstreams={workstreams}
                existingNames={existingNames}
                saving={saving}
                onSave={handleSave}
                onCancel={closeForm}
            />
        );
    }

    return (
        <NewProjectWizardCard
            step={1}
            totalSteps={TEAM_SETUP_STEPS}
            accessWide
            title="Set up access groups"
            subtitle="Create groups to define the access that team members will inherit."
            hint="You can change access and configure individual exceptions later from the project."
            continueLabel="Continue"
            onContinue={onContinue}
            onBack={onBack}
            backLabel="Back to Project Ready"
            backInline
        >
            <p className={styles.agGroupLabel}>Project Administrators</p>
            <div className={styles.agAdminCard}>
                <div className={styles.agAdminTop}>
                    <span className={styles.agAvatar} aria-hidden="true">
                        {initials}
                    </span>
                    <div>
                        <p className={styles.agAdminName}>
                            {displayName || email || 'You'}
                            <span className={styles.wsRecommendedBadge}>You</span>
                        </p>
                        {email ? <p className={styles.agAdminEmail}>{email}</p> : null}
                    </div>
                </div>
                <p className={styles.agAdminHint}>
                    Project Administrators can manage project setup, team members and access.
                </p>
            </div>

            <p className={`${styles.agGroupLabel} ${styles.agGroupsLabel}`}>Access groups</p>
            {accessGroups.length === 0 ? (
                <div className={styles.agEmpty}>
                    <p className={styles.agEmptyTitle}>No access groups created yet</p>
                    <p className={styles.agEmptyHint}>
                        Create groups for teams, advisers, management or other project participants.
                    </p>
                    <button type="button" className={styles.agOutlineButton} onClick={openCreateForm}>
                        + Create access group
                    </button>
                </div>
            ) : (
                <>
                    <div className={styles.agGroupList}>
                        {accessGroups.map(group => {
                            const extra = extras[group.id] || draftFromGroup(group, workstreams);
                            return (
                                <div key={group.id} className={styles.agGroupCard}>
                                    <div className={styles.agGroupHeader}>
                                        <h3 className={styles.agGroupName}>{group.name}</h3>
                                        <div className={styles.agGroupActions}>
                                            <button type="button" className={styles.agTextLink} onClick={() => startEdit(group)}>
                                                Edit
                                            </button>
                                            <button type="button" className={styles.agTextLink} onClick={() => handleRemove(group)}>
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                    <dl className={styles.agMeta}>
                                        <div className={styles.agMetaRow}>
                                            <dt>Workspace</dt>
                                            <dd>{featureSummary(extra.features) || '—'}</dd>
                                        </div>
                                        <div className={styles.agMetaRow}>
                                            <dt>Workstreams</dt>
                                            <dd>{workstreamSummary(extra, workstreams)}</dd>
                                        </div>
                                        <div className={styles.agMetaRow}>
                                            <dt>Intelligence Insights</dt>
                                            <dd className={extra.intelligence ? undefined : styles.agMetaMuted}>
                                                {extra.intelligence ? 'Access granted' : 'No access'}
                                            </dd>
                                        </div>
                                        <div className={styles.agMetaRow}>
                                            <dt>Project Mail</dt>
                                            <dd className={extra.projectMail ? undefined : styles.agMetaMuted}>
                                                {extra.projectMail ? 'Access granted' : 'No access'}
                                            </dd>
                                        </div>
                                        <div className={styles.agMetaRow}>
                                            <dt>Project Meetings</dt>
                                            <dd className={extra.projectMeetings ? undefined : styles.agMetaMuted}>
                                                {extra.projectMeetings ? 'Access granted' : 'No access'}
                                            </dd>
                                        </div>
                                        <div className={styles.agMetaRow}>
                                            <dt>VDR</dt>
                                            <dd>{vdrPresetTitle(extra.vdrPreset)}</dd>
                                        </div>
                                        <div className={styles.agMetaRow}>
                                            <dt>Members</dt>
                                            <dd className={styles.agMetaMuted}>No members added yet</dd>
                                        </div>
                                    </dl>
                                </div>
                            );
                        })}
                    </div>
                    <button type="button" className={`${styles.addScopeLink} ${styles.agAddLink}`} onClick={openCreateForm}>
                        + Create access group
                    </button>
                </>
            )}
        </NewProjectWizardCard>
    );
}
